// The one way a promo code gets redeemed: POST /api/promo/redeem for an email.
// Used by PendingCodeRedeemer (a code typed before sign-in) and PromoCodeField
// (a code typed while signed in). Writes what the rest of the app reads:
//   localStorage ttt_promo_grant  the banner on /timo (promoGrant.js)
//   sessionStorage ttt_promo_error the note on /pricing (Pricing.jsx)
//   localStorage ttt_has_plan      the per-device fallback AuthContext reads
import posthog from 'posthog-js';
import { API_BASE_URL } from '@/lib/apiBaseUrl';
import { normalizePromoCode } from '@/lib/promoCodes';
import { rememberPromoResult } from '@/lib/promoGrant';

export const PROMO_ERROR_KEY = 'ttt_promo_error';

// A school wifi can hold a request open for minutes. After this long the
// redeem counts as a network failure and the person goes on to their normal
// page instead of a spinner.
export const REDEEM_TIMEOUT_MS = 12000;

const TIMED_OUT = Symbol('timed out');

// One deadline over the whole exchange, the reply and its body. A plain
// AbortController plus setTimeout instead of AbortSignal.timeout: it works in
// every browser that has fetch, and it also cuts a body that never arrives.
const startDeadline = (ms) => {
  let ctrl = null;
  try { ctrl = new AbortController(); } catch { /* very old browser: no abort, the race still ends it */ }
  let timer;
  const expired = new Promise((resolve) => {
    timer = setTimeout(() => {
      try { ctrl?.abort(); } catch { /* noop */ }
      resolve(TIMED_OUT);
    }, ms);
  });
  return {
    signal: ctrl?.signal,
    race: (p) => Promise.race([p, expired]),
    clear: () => clearTimeout(timer),
  };
};

const writeError = (code, errorText, alreadyRedeemed) => {
  try {
    sessionStorage.setItem(PROMO_ERROR_KEY, JSON.stringify({ code, errorText, alreadyRedeemed }));
  } catch {
    /* storage blocked: the paywall just shows no note */
  }
};

const track = (event, props) => {
  try { posthog?.capture(event, props); } catch { /* analytics never breaks a redeem */ }
};

// Server replies (server/index.js, POST /api/promo/redeem) -> one word.
const classify = (status, body) => {
  if (body?.alreadyRedeemed) {
    return /network/i.test(String(body.error || '')) ? 'usedOnNetwork' : 'usedOnAccount';
  }
  if (body?.capReached) return 'fullyClaimed';
  if (status === 400 && /invalid/i.test(String(body?.error || ''))) return 'invalid';
  return 'error';
};

/**
 * @returns {Promise<{ok:boolean, kind:string, code?:string, days?:number, expiresAt?:string}>}
 * kind: granted | alreadyActive | usedOnAccount | usedOnNetwork | fullyClaimed
 *       | invalid | network | error
 */
// storeError: false when the person is looking at the result right here
// (PromoCodeField), so no stale note waits for them on the next /pricing.
export async function redeemPromoCode({ email, code, checkSubscriptionStatus, fetchImpl, method = 'app', storeError = true } = {}) {
  const c = normalizePromoCode(code);
  const mail = typeof email === 'string' ? email.trim() : '';
  if (!c || !mail) return { ok: false, kind: 'invalid', code: c };

  const saveError = (...a) => { if (storeError) writeError(...a); };
  const networkFailure = (error) => {
    track('promo_redeem_error', { code: c, method, error });
    saveError(c, 'network', false);
    return { ok: false, kind: 'network', code: c };
  };

  const doFetch = fetchImpl || ((...a) => fetch(...a));
  const deadline = startDeadline(REDEEM_TIMEOUT_MS);
  let resp;
  let body = {};
  try {
    resp = await deadline.race(doFetch(`${API_BASE_URL}/api/promo/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: mail, code: c }),
      signal: deadline.signal,
    }));
    if (resp === TIMED_OUT) return networkFailure('timeout');
    let parsed;
    try {
      parsed = await deadline.race(Promise.resolve().then(() => resp.json()));
    } catch {
      parsed = {};
    }
    if (parsed === TIMED_OUT) return networkFailure('timeout');
    body = parsed || {};
  } catch (err) {
    return networkFailure(String(err));
  } finally {
    deadline.clear();
  }

  rememberPromoResult(c, body);

  if (body.success === true) {
    track('promo_redeemed', { code: c, method, expires_at: body.expiresAt });
    try { localStorage.setItem('ttt_has_plan', 'premium'); } catch { /* noop */ }
    try { await checkSubscriptionStatus?.(); } catch { /* the guard re-checks on its own */ }
    if (body.alreadyActive) return { ok: true, kind: 'alreadyActive', code: c };
    return { ok: true, kind: 'granted', code: c, days: body.days, expiresAt: body.expiresAt };
  }

  track('promo_redeem_failed', { code: c, method, error: body.error });
  saveError(c, body.error || '', !!body.alreadyRedeemed);
  return { ok: false, kind: classify(resp?.status, body), code: c };
}
